import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { cssMap } from '@compiled/react';

import Button from '@atlaskit/button/default/button';
import EditIcon from '@atlaskit/icon/core/edit';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Text } from '@atlaskit/primitives/compiled/text';
import { token } from '@atlaskit/tokens';

const i18n = defineMessages({
	editModeBannerMessage: {
		id: 'suggestions-panel.published.edit-mode-banner.message',
		defaultMessage: 'View suggestions in edit mode',
		description:
			'Message in the banner at the top of the AI suggestions panel on a published page, explaining that suggestions are reviewed in edit mode',
	},
	editModeBannerButton: {
		id: 'suggestions-panel.published.edit-mode-banner.button',
		defaultMessage: 'Edit',
		description:
			'Button in the banner at the top of the AI suggestions panel on a published page that opens the page in edit mode',
	},
});

const styles = cssMap({
	banner: {
		backgroundColor: token('color.background.information'),
		borderRadius: token('radius.large'),
		paddingBlock: token('space.100'),
		paddingInlineStart: token('space.200'),
		paddingInlineEnd: token('space.100'),
	},
});

type SuggestionsEditModeBannerProps = {
	/** Opens the page in edit mode, where suggestions can be accepted or discarded. */
	onEdit: () => void;
};

export const SuggestionsEditModeBanner = ({
	onEdit,
}: SuggestionsEditModeBannerProps): React.JSX.Element => {
	const { formatMessage } = useIntl();

	return (
		<div css={styles.banner} data-testid="published-suggestions-edit-mode-banner">
			<Inline space="space.100" alignBlock="center" spread="space-between">
				<Text as="p" weight="semibold">
					{formatMessage(i18n.editModeBannerMessage)}
				</Text>
				<Button iconBefore={EditIcon} onClick={() => onEdit()}>
					{formatMessage(i18n.editModeBannerButton)}
				</Button>
			</Inline>
		</div>
	);
};
